import { Link } from 'react-router-dom';
import type { Post } from '@/types/content';
import { formatDate, formatViews, getCategoryBySlug, toneClasses } from '@/utils/content';

type Variant = 'feature' | 'default' | 'row' | 'compact';

interface PostCardProps {
  post: Post;
  variant?: Variant;
  index?: number;
}

export default function PostCard({ post, variant = 'default', index = 0 }: PostCardProps) {
  const category = getCategoryBySlug(post.categorySlug);
  const tone = toneClasses[category?.tone ?? 'primary'];

  if (variant === 'row') {
    return (
      <Link
        to={`/post/${post.slug}`}
        className="group flex gap-3 py-4 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        <span className="hidden sm:flex w-7 h-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-xs font-semibold text-foreground-600 mt-0.5">
          {index + 1}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm md:text-base font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
            {post.title}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{category?.name}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(post.views)}
            </span>
          </span>
        </span>
        <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors self-center"></i>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/post/${post.slug}`}
        className="group block p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${tone.soft}`}>
          {category?.name}
        </span>
        <h4 className="mt-2.5 text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
          {post.title}
        </h4>
        <span className="mt-2 block text-xs text-foreground-500">{formatDate(post.publishedAt)}</span>
      </Link>
    );
  }

  if (variant === 'feature') {
    return (
      <Link
        to={`/post/${post.slug}`}
        className="group grid grid-cols-1 lg:grid-cols-2 rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full h-56 md:h-72 lg:h-full lg:min-h-[340px] overflow-hidden bg-background-100">
          <img
            src={post.cover}
            alt={post.coverAlt}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
        <div className="p-6 md:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${tone.soft}`}>
              {category?.name}
            </span>
            {post.hot && (
              <span className="rounded-full px-3 py-1 text-xs font-medium bg-primary-500 text-background-50">
                热榜
              </span>
            )}
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
            {post.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span>{post.author}</span>
            <span>{formatDate(post.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(post.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {post.readMinutes} 分钟
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/post/${post.slug}`}
      className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full h-44 md:h-48 overflow-hidden bg-background-100">
        <img
          src={post.cover}
          alt={post.coverAlt}
          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2.5">
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${tone.soft}`}>
            {category?.name}
          </span>
          {post.hot && (
            <span className="text-[11px] text-primary-600 flex items-center gap-0.5">
              <i className="ri-fire-line"></i>
              热门
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-medium text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
          {post.excerpt}
        </p>
        <div className="mt-4 pt-3 border-t border-background-100 flex items-center justify-between text-xs text-foreground-500">
          <span>{formatDate(post.publishedAt)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(post.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}